import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * PrepScene — "Little Words, Big Map" hook for the prepositions lesson.
 *
 * Four tiles, one per preposition (فِي، عَلَى، مِنْ، إِلَى). Tapping a tile
 * reveals a mini scene diagram showing what the word does — a dot in/on a box,
 * an arrow leaving or arriving — plus an example sentence with audio and a
 * literal gloss. Completes when every preposition has been explored.
 */
function SceneDiagram({ scene }) {
  const box = { stroke: "var(--gold-dim)", fill: "none", strokeWidth: 2.5 };
  const dot = { fill: "var(--tag-violet)" };
  const arrow = { stroke: "var(--gold-bright)", strokeWidth: 2.5, strokeLinecap: "round" };
  return (
    <svg viewBox="0 0 96 56" className="w-24 h-14 shrink-0" aria-hidden="true">
      {scene === "in" && (
        <>
          <path d="M30 18 v24 a4 4 0 0 0 4 4 h28 a4 4 0 0 0 4 -4 v-24" {...box} />
          <circle cx="48" cy="34" r="7" {...dot} />
        </>
      )}
      {scene === "on" && (
        <>
          <rect x="30" y="28" width="36" height="18" rx="4" {...box} />
          <circle cx="48" cy="21" r="7" {...dot} />
        </>
      )}
      {scene === "from" && (
        <>
          <rect x="8" y="16" width="26" height="24" rx="4" {...box} />
          <path d="M40 28 h34" {...arrow} fill="none" />
          <path d="M67 21 l9 7 -9 7" {...arrow} fill="none" strokeLinejoin="round" />
          <circle cx="85" cy="28" r="6" {...dot} />
        </>
      )}
      {scene === "to" && (
        <>
          <circle cx="11" cy="28" r="6" {...dot} />
          <path d="M22 28 h32" {...arrow} fill="none" />
          <path d="M47 21 l9 7 -9 7" {...arrow} fill="none" strokeLinejoin="round" />
          <rect x="62" y="16" width="26" height="24" rx="4" {...box} />
        </>
      )}
    </svg>
  );
}

export default function PrepScene({ data, onComplete, audio }) {
  const [openedIds, setOpenedIds] = useState(() => new Set());

  const items = data.items;
  const allOpened = openedIds.size >= items.length;

  useEffect(() => {
    if (allOpened) onComplete();
  }, [allOpened, onComplete]);

  const handleOpen = (item) => {
    if (openedIds.has(item.id)) return;
    setOpenedIds((prev) => new Set(prev).add(item.id));
    if (audio) audio.play(item.sentence_ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{openedIds.size}/{items.length} explored</ProgressPill>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((item) => {
          const opened = openedIds.has(item.id);
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 py-4 flex flex-col gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <button
                type="button"
                onClick={() => handleOpen(item)}
                disabled={opened}
                className={`flex items-baseline justify-center gap-3 rounded-lg px-3 py-2 transition-all ${opened ? "" : "hover:brightness-125 animate-pulse cursor-pointer"}`}
                style={
                  opened
                    ? { background: "transparent", border: "1px solid transparent" }
                    : { background: "var(--gold-bg)", border: "1px dashed var(--gold-dim)" }
                }
                title={opened ? undefined : "Tap to see what this little word does"}
              >
                <span dir="rtl" lang="ar" className="font-arabic text-4xl leading-[1.5]" style={{ color: "var(--gold-bright)" }}>
                  {item.prep_ar}
                </span>
                <span className="text-sm italic" style={{ color: "var(--gold-dim)" }}>{item.translit}</span>
                <span className="text-base font-semibold" style={{ color: "var(--parchment)" }}>{item.prep_en}</span>
              </button>

              {opened && (
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <SceneDiagram scene={item.scene} />
                  <p className="m-0 flex items-center gap-3">
                    <span dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.7]" style={{ color: "var(--parchment)" }}>
                      {item.sentence_ar}
                    </span>
                    {audio && (
                      <PlayButton text={item.sentence_ar} playingText={audio.playingText} onPlay={audio.play} />
                    )}
                  </p>
                  <p className="text-sm m-0" style={{ color: "var(--parchment-dim)" }}>{item.sentence_en}</p>
                  <p className="text-xs m-0 italic" style={{ color: "var(--gold-dim)" }}>
                    literally: {item.literal_en}
                  </p>
                  {item.note && (
                    <p className="text-xs mt-1 mb-0 leading-relaxed" style={{ color: "var(--parchment-dim)" }}>
                      <MixedText text={item.note} />
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allOpened && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
